import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import StatsCard from '../components/ui/StatsCard';
import DayStrip from '../components/health/DayStrip';
import { adminApi } from '../config/axios';
import { useDocumentTitle } from '../config/utils';
import { formatHealthTime, formatHealthFull, tzHeaderLabel } from '../utils/healthTime';
import {
  FLEET_FILTERS, healthStatusVariant, sortAttention, filterAttention,
} from '../utils/healthStyle';
import { describeKey, describeHeadline, describeReason, statusLabel } from '../config/healthText';
import { connectivityBadge } from '../utils/connectivityBadge';

const unwrap = (res) => res?.data?.data ?? res?.data ?? null;

// KPI tiles -> list filter (null = informational only).
const KPIS = [
  { key: 'working', title: 'Working', color: 'teal', filter: null },
  { key: 'problem', title: 'Problem now', color: 'gold', filter: 'problem' },
  { key: 'offline', title: 'Offline', color: 'danger', filter: 'offline' },
  { key: 'recovered_today', title: 'Recovered today', color: 'accent', filter: 'recovered' },
  { key: 'legacy', title: 'Legacy', color: 'muted', filter: 'legacy' },
  { key: 'outdated_version', title: 'Outdated version', color: 'gold', filter: null },
];

export default function DeviceHealth() {
  useDocumentTitle('Device Health');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState('all');
  const [companyId, setCompanyId] = useState('');
  const [search, setSearch] = useState('');
  const [showWorking, setShowWorking] = useState(false);
  // build 404 -- the admin bell links here with ?device=<id>: that device is SELECTED (the list narrows to it, its row is highlighted and a banner offers its page).
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('device') || '';
  const clearSelected = () => { const next = new URLSearchParams(searchParams); next.delete('device'); setSearchParams(next, { replace: true }); };

  const load = useCallback(async (initial) => {
    try {
      const res = await adminApi.get('/devices/health/fleet');
      const d = unwrap(res);
      setData(d);
      setError(false);
    } catch {
      if (initial) setError(true);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 30000);
    return () => clearInterval(t);
  }, [load]);

  const attention = useMemo(() => sortAttention(data?.attention ?? []), [data]);
  const rows = useMemo(() => (selectedId
    ? attention.filter((r) => String(r.id) === String(selectedId))
    : filterAttention(attention, { status, companyId, search })), [attention, status, companyId, search, selectedId]);
  // Every company with devices in scope comes from the payload (not just the
  // ones that currently have an attention row).
  const options = useMemo(() => (data?.companies ?? [])
    .map((c) => ({ id: String(c.id), name: c.name ?? `#${c.id}` }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name))), [data]);

  const companyName = companyId ? options.find((o) => o.id === String(companyId))?.name : null;
  const q = search.trim().toLowerCase();
  const workingList = (data?.working?.devices ?? [])
    .filter((w) => !companyName || w.company === companyName)
    .filter((w) => !q || `${w.name ?? ''} ${w.company ?? ''}`.toLowerCase().includes(q));
  const workingCount = data?.working?.count ?? 0;

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Device Health</h1>
          <p className="text-text-muted text-sm mt-0.5">Fleet health · auto-refresh every 30s · times in {tzHeaderLabel(undefined)} (your timezone — the fleet list carries no per-device timezone)</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      ) : error || !data ? (
        <div className="card-padded text-center text-text-muted">
          Could not load fleet health.{' '}
          <button className="text-accent underline" onClick={() => { setLoading(true); setError(false); load(true); }}>Retry</button>
        </div>
      ) : (
        <>
          <p className="text-base font-semibold text-text-main mb-3" data-testid="online-now">
            Online now {data.kpis?.online_now ?? 0} of {data.kpis?.total ?? 0}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
            {KPIS.map((k) => (
              <div key={k.key}
                onClick={k.filter ? () => setStatus(status === k.filter ? 'all' : k.filter) : undefined}
                className={`${k.filter ? 'cursor-pointer' : ''} ${k.filter && status === k.filter ? 'ring-1 ring-accent rounded-2xl' : ''}`}
                title={k.key === 'outdated_version' ? 'Count only — outdated devices may also be healthy, so no list filter' : undefined}>
                <StatsCard title={k.title} value={kpiValue(k, data.kpis)} color={k.color} />
              </div>
            ))}
          </div>

          {selectedId && (
            <div data-testid="selected-device" className="card-padded mb-4 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-text-main">
                Showing device <strong>{attention.find((r) => String(r.id) === String(selectedId))?.name ?? (data?.working?.devices ?? []).find((w) => String(w.id) === String(selectedId))?.name ?? `#${selectedId}`}</strong>
                {attention.some((r) => String(r.id) === String(selectedId)) ? '' : ' — it is not in the attention list right now'}
              </span>
              <Link data-testid="selected-device-open" className="text-accent hover:underline text-sm" to={`/devices/${selectedId}`}>Open the device page</Link>
              <button data-testid="selected-device-clear" className="text-sm text-text-muted underline" onClick={clearSelected}>Show all devices</button>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex gap-1 bg-bg-card border border-white/[0.08] rounded-lg p-1">
              {FLEET_FILTERS.map((f) => (
                <button key={f.key} onClick={() => setStatus(f.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${status === f.key ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <select className="input-base py-2 text-sm w-auto" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">All companies</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <input type="text" placeholder="Search device, company…" value={search}
              onChange={(e) => setSearch(e.target.value)} className="input-base flex-1 max-w-xs py-2 text-sm" />
          </div>

          <div className="card mb-5 overflow-x-auto">
            {rows.length === 0 ? (
              <div className="text-center py-10 text-text-muted text-sm">
                {selectedId ? 'This device is not in the attention list right now.' : (attention.length === 0 ? 'No devices need attention.' : 'No devices match your filter.')}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-text-muted uppercase">
                  <th className="p-3">Device</th><th>Company</th><th>Status</th><th>Reason</th>
                  <th className="pr-6 min-w-[7rem]">Since</th><th className="pr-6 min-w-[7rem]">Last signal</th><th>App</th><th>7 days</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={String(r.id) === String(selectedId) ? 'row-selected' : undefined}
                      className={`border-t border-white/[0.05] ${String(r.id) === String(selectedId) ? 'bg-accent/10 ring-1 ring-accent' : ''}`}>
                      <td className="p-3"><Link className="text-accent hover:underline" to={`/devices/${r.id}`}>{r.name ?? `#${r.id}`}</Link>
                        {r.branch && <div className="text-xs text-text-muted">{r.branch}</div>}</td>
                      <td>{r.company ?? '—'}</td>
                      <td><StatusPill row={r} /></td>
                      <td className="text-text-muted"><ReasonCell row={r} /></td>
                      <td className="whitespace-nowrap pr-6 min-w-[7rem]" title={timeTitle(r.since)}>{formatHealthTime(r.since) || '—'}</td>
                      <td className="whitespace-nowrap pr-6 min-w-[7rem]" title={timeTitle(r.last_signal_at)}>{formatHealthTime(r.last_signal_at) || '—'}</td>
                      <td>{r.app_version ?? '—'}</td>
                      <td><DayStrip days={r.days} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card-padded mb-5">
            <button className="text-sm font-semibold text-text-main" onClick={() => setShowWorking((v) => !v)} data-testid="working-toggle">
              {showWorking ? '▾' : '▸'} {workingLabel(workingCount)}
            </button>
            {showWorking && (
              <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1 text-sm">
                {workingList.map((w) => (
                  <li key={w.id}><Link className="text-accent hover:underline" to={`/devices/${w.id}`}>{w.name ?? `Device #${w.id}`}</Link>
                    {w.company && <span className="text-text-muted"> · {w.company}</span>}</li>
                ))}
                {workingList.length === 0 && <li className="text-text-muted">No devices to show.</li>}
              </ul>
            )}
          </div>

          <div className="card-padded">
            <h3 className="section-title mb-3">Top issues — last 7 days</h3>
            {(data.top_issues_7d ?? []).length === 0 ? (
              <p className="text-sm text-text-muted">No issues recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-text-muted uppercase"><th className="py-1">Issue</th><th>Incidents</th><th>Devices</th></tr></thead>
                <tbody>
                  {data.top_issues_7d.map((t) => (
                    <tr key={t.template_key} className="border-t border-white/[0.05]">
                      <td className="py-1.5">{issueLabel(t)}</td><td>{t.incidents ?? t.events}</td><td>{t.devices}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}

const timeTitle = (iso) => (iso ? `${formatHealthFull(iso)} (your timezone)` : undefined);

export const workingLabel = (n) => `${n} ${Number(n) === 1 ? 'device' : 'devices'} working normally`;

export function kpiValue(k, kpis) {
  const v = kpis?.[k.key] ?? 0;
  if (k.key === 'legacy' && kpis?.legacy_online != null) return `${v} (${kpis.legacy_online} online)`;
  return v;
}

export const issueLabel = (t) => describeKey(t.label_key ?? `fault.${t.template_key}`, {});

// Short headline (viewer-local time); falls back to the reason for older payloads.
export function reasonText(row, now) {
  const ctx = { tz: undefined, now };
  const h = describeHeadline(row.headline, ctx);
  if (h) return h;
  return row.legacy ? describeKey('status.legacy') : (typeof row.reason === 'string' ? describeKey(row.reason, {}, ctx) : describeReason(row.reason, ctx));
}

function ReasonCell({ row }) {
  const text = reasonText(row);
  return <div className="max-w-xs truncate" title={text} data-testid="reason-cell">{text}</div>;
}

// A legacy device shows its real presence PLUS an "Old app" tag, so an online
// legacy device does not look missing.
export function StatusPill({ row }) {
  if (row.legacy || row.status === 'legacy') {
    const b = connectivityBadge(row.presence ?? 'offline');
    return (
      <span className="inline-flex items-center gap-1.5">
        <Badge variant={b.variant} dot>{b.label}</Badge>
        <Badge variant="muted">Old app</Badge>
      </span>
    );
  }
  return <Badge variant={healthStatusVariant(row.status)} dot>{statusLabel(row.status)}</Badge>;
}
